import { Injectable, inject, signal } from '@angular/core';
import { AGENTIC_CHAT_PANEL_STORAGE_KEY } from '../../constants/agentic-chat.constants';
import { environment } from '../../environments/environment';
import type { AgenticChatUiVariant } from '../../interfaces/agentic-chat/agentic-chat-config.interface';
import type { AgenticChatEnvironment } from '../../interfaces/agentic-chat/agentic-chat-config.interface';
import { readAgentIdFromRunsUrl } from '../../utils/agentic-chat-platform.utils';
import { AuthServiceBase } from '../auth/auth.service.base';
import { AuthTokenRefreshService } from '../auth/auth-token-refresh.service';

@Injectable({ providedIn: 'root' })
export class AgenticChatService {
  private readonly auth = inject(AuthServiceBase);
  private readonly tokenRefresh = inject(AuthTokenRefreshService);

  private readonly isOpenState = signal(false);

  readonly isOpen = this.isOpenState.asReadonly();

  constructor() {
    this.restorePanelState();
  }

  get enabled(): boolean {
    return environment.agenticChat?.enabled === true && !!this.agentId;
  }

  get runsUrl(): string {
    return environment.agenticChat?.runsUrl?.trim() ?? '';
  }

  get agentId(): string {
    return readAgentIdFromRunsUrl(this.runsUrl);
  }

  get environment(): AgenticChatEnvironment {
    return environment.agenticChat?.environment ?? 'development';
  }

  get uiVariant(): AgenticChatUiVariant {
    return environment.agenticChat?.uiVariant ?? 'narrow';
  }

  get hideModelSelection(): boolean {
    return environment.agenticChat?.hideModelSelection ?? true;
  }

  toggle(): void {
    if (this.isOpenState()) {
      this.close();
      return;
    }
    this.open();
  }

  open(): void {
    if (!this.enabled) {
      return;
    }
    this.isOpenState.set(true);
    this.persistOpen(true);
  }

  close(): void {
    this.isOpenState.set(false);
    this.persistOpen(false);
  }

  async getAccessToken(): Promise<string> {
    await this.tokenRefresh.ensureValidTokens();
    return this.auth.accessToken ?? '';
  }

  private restorePanelState(): void {
    try {
      const stored = sessionStorage.getItem(AGENTIC_CHAT_PANEL_STORAGE_KEY);
      if (stored === 'true' && this.enabled) {
        this.isOpenState.set(true);
      }
    } catch {
      // ignore storage errors
    }
  }

  private persistOpen(open: boolean): void {
    try {
      sessionStorage.setItem(AGENTIC_CHAT_PANEL_STORAGE_KEY, open ? 'true' : 'false');
    } catch {
      // ignore storage errors
    }
  }
}
